import React, { useState, useMemo, useEffect } from 'react'
import Select from 'react-select'
import { useIntl } from 'react-intl'
import _ from 'lodash'
import * as styles from './index.module.css'
import SEO from '../SEO'
import MainBlackHeader from '../MainBlackHeader'
import DownloadAlertBar from '../DownloadAlertBar'
import DownloadList from '../DownloadList'
import DropdownIndicator from '../DownloadDropdownIndicator'
import Tooltip from '../DownloadTooltip'
import SensorUpdateProcess from '../SensorUpdateProcess'

const DownloadCenter = ({ data }) => {
  // Names
  const intl = useIntl()
  const downloadTitle = intl.formatMessage({ id: 'downloads.title' })
  const seoTitle = intl.formatMessage({ id: 'downloads.seoTitle' })
  const ogDescription = intl.formatMessage({ id: 'downloads.ogDescription' })
  const hardwareVersion = intl.formatMessage({
    id: 'downloads.hardwareVersion',
  })
  const firmwareVersion = intl.formatMessage({
    id: 'downloads.firmwareVersion',
  })

  // Tooltips
  const tooltipHardware = data[2].edges[1].node
  const TooltipHardwareHTML = `<p>${tooltipHardware.tooltipText}</p>
      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="${tooltipHardware.tooltipLink}"
          className="link"
        >
          ${tooltipHardware.tooltipButtonText}
        </a>
      </p>`
  const tooltipFirmware = data[2].edges[0].node
  const TooltipFirmwareHTML = `<p>${tooltipFirmware.tooltipText}</p>
      <p>
        <a
          target="_blank"
          rel="noreferrer"
          href="${tooltipFirmware.tooltipLink}"
          className="link"
        >
          ${tooltipFirmware.tooltipButtonText}
        </a>
      </p>`

  // Styles for select list
  const customStyles = useMemo(
    () => ({
      option: (styles, { isSelected, isFocused, isDisabled }) => {
        let backgroundColor = '#FFFFFF'
        let color = '#000000'
        let borderColor = '#C6C6C6'

        if (isSelected) {
          backgroundColor = '#FFFFFF'
          color = '#000000'
          borderColor = '#000000'
        }

        if (isFocused) {
          backgroundColor = '#FFFFFF'
          color = '#000000'
        }

        if (isDisabled) {
          backgroundColor = '#FFFFFF'
          color = '#C6C6C6'
        }

        return {
          ...styles,
          width: '100%',
          opacity: 1,
          border: '1px solid #C6C6C6',
          borderColor,
          paddingTop: 12,
          paddingBottom: 12,
          paddingLeft: '14px',
          backgroundColor,
          color,
          marginTop: 'px',
          '&:active': {
            // Overwrittes the different states of border
            background: '#fff',
          },
        }
      },
      control: (styles) => {
        return {
          ...styles,
          width: 300,
          minHeight: 52,
          boxShadow: 'none',
          borderColor: '#5E79FB',
        }
      },
      menu: (styles) => {
        return {
          ...styles,
          width: 300,
          paddingTop: 0,
          marginTop: '-1px',
          minHeight: 52,
          boxShadow: 'none',
        }
      },
      menuList: (styles) => {
        return { ...styles, paddingTop: 0, paddingBottom: 0, minHeight: 52 }
      },
      singleValue: (styles, state) => {
        return { ...styles, marginLeft: '7px' }
      },
      indicatorSeparator: (styles, state) => {
        return { ...styles, border: 'none', backgroundColor: '#fff' }
      },
      indicatorContainer: (styles, state) => {
        return {
          ...styles,
          border: 'none',
          backgroundColor: '#fff',
          flexDirection: 'column',
        }
      },
    }),
    []
  )

  useEffect(() => {
    return () => {}
  }, [])

  // Set order of options
  const compare = (a, b) => {
    if (!a.node || !b.node) return
    if (a.node.order < b.node.order) {
      return -1
    }
    if (a.node.order > b.node.order) {
      return 1
    }
    return 0
  }

  // Build options array for firmware
  const firmwareOptionsData = data[3].edges
    .filter((node) => node.node.type === 'Firmware')
    .sort(compare)
    .map((node) => {
      return {
        value: node.node.value,
        label: node.node.label,
        incompatible: node.node.incompatible,
        isDisabled: false,
      }
    })

  // Build options array for hardware
  const hardwareOptionsData = data[3].edges
    .filter((node) => node.node.type === 'Hardware')
    .sort(compare)
    .map((node) => {
      return {
        value: node.node.value,
        label: node.node.label,
        incompatible: node.node.incompatible,
        isDisabled: false,
      }
    })

  // Options for select list
  const firmwareOptions = useMemo(() => firmwareOptionsData, [])
  const hardwareOptions = useMemo(() => hardwareOptionsData, [])

  // Reflow function to get newly disabled options
  const getDisabledHardwareOptions = () => {
    return hardwareOptions.map((option) => ({
      ...option,
      isDisabled: option.disabled === true,
    }))
  }
  const getDisabledFirmwareOptions = () => {
    return firmwareOptions.map((option) => ({
      ...option,
      isDisabled: option.disabled === true,
    }))
  }

  // State Changes
  const [selectValueHardware, setSelectValueHardware] = useState(
    hardwareOptions[0]
  )
  const [selectValueFirmware, setSelectValueFirmware] = useState(
    firmwareOptions[0]
  )

  const [mappedFirmwareOptions, setMappedFirmwareOptions] = useState(
    getDisabledFirmwareOptions(firmwareOptions)
  )
  const [mappedHardwareOptions, setMappedHardwareOptions] = useState(
    getDisabledHardwareOptions(hardwareOptions)
  )

  // Data
  const sensorData = data[4]
  const dataByType = useMemo(() => {
    const sortedEdges = data[0].edges.slice().sort((a, b) => {
      if (a.node.groupType.order !== b.node.groupType.order)
        return a.node.groupType.order - b.node.groupType.order
      return a.node.order - b.node.order
    })
    const groupedData = _.groupBy(
      sortedEdges,
      (item) => item.node.groupType.groupType
    )

    for (const type in groupedData) {
      let labels = []
      groupedData[type].forEach((edge) => {
        if (
          labels.length < edge.node.downloadCenterTableData.tableData[0].length
        ) {
          labels = edge.node.downloadCenterTableData.tableData[0]
        }
        edge.node.downloadCenterDataByType = []
        edge.node.downloadCenterTableData.tableData.slice(1).forEach((item) => {
          const tableData = {}
          for (let index = 0; index < item.length; index += 1) {
            tableData[
              edge.node.downloadCenterTableData.tableData[0][
                index
              ].toLowerCase()
            ] = item[index]
          }
          edge.node.downloadCenterDataByType.push(tableData)
        })
      })
      groupedData[type].push(labels)
    }

    return groupedData
  }, [data])

  const filteredData = useMemo(() => {
    const newData = {}

    for (const type in dataByType) {
      const labels = dataByType[type][dataByType[type].length - 1]
      const values = []

      dataByType[type].slice(0, dataByType[type].length - 1).forEach((edge) => {
        const filteredDownloadCenterData =
          edge.node.downloadCenterDataByType.filter((item) => {
            return (
              (!item.hardware ||
                item.hardware.includes(selectValueHardware.value)) &&
              (!item.firmware ||
                item.firmware.includes(selectValueFirmware.value))
            )
          })
        if (filteredDownloadCenterData.length > 0) {
          const downloadCenterData = _.cloneDeep(filteredDownloadCenterData[0])
          delete downloadCenterData.hardware
          delete downloadCenterData.firmware
          values.push({
            id: edge.node.id,
            title: edge.node.title,
            data: downloadCenterData,
          })
        }
      })

      if (values.length > 0) {
        newData[type] = {
          labels: labels.filter(
            (label) => label !== 'Hardware' && label !== 'Firmware'
          ),
          values,
        }
      }
    }

    return newData
  }, [dataByType, selectValueHardware, selectValueFirmware])

  // Events
  const handleHardwareChange = (option) => {
    setSelectValueHardware(option)
    setMappedHardwareOptions(getDisabledHardwareOptions(option))
    // Disable any options that are incompatible
    firmwareOptions.forEach((firmware, index) => {
      firmware.disabled = false
      setMappedFirmwareOptions(
        getDisabledFirmwareOptions(mappedFirmwareOptions[index])
      )
      option.incompatible !== null &&
        option.incompatible.forEach((option, index) => {
          if (firmware.value === option.value) {
            firmware.disabled = true
            setMappedFirmwareOptions(
              getDisabledFirmwareOptions(mappedFirmwareOptions[index])
            )
          }
        })
    })
  }

  const handleFirmwareChange = (option) => {
    setSelectValueFirmware(option)
    setMappedFirmwareOptions(getDisabledFirmwareOptions(option))
    // Disable any options that are incompatible
    hardwareOptions.forEach((hardware, index) => {
      hardware.disabled = false
      setMappedHardwareOptions(
        getDisabledHardwareOptions(mappedHardwareOptions[index])
      )
      option.incompatible !== null &&
        option.incompatible.forEach((option, index) => {
          if (hardware.value === option.value) {
            hardware.disabled = true
            setMappedHardwareOptions(
              getDisabledHardwareOptions(mappedHardwareOptions[index])
            )
          }
        })
    })
  }

  useEffect(() => {
    // Trigger options logic on load
    handleFirmwareChange(mappedFirmwareOptions[0])
    handleHardwareChange(mappedHardwareOptions[0])
  }, [])

  // Check if option is disabled
  const isOptionDisabled = (option) => option.disabled

  return (
    <>
      <SEO
        {...data}
        type="downloadCenter"
        slug="/downloads/"
        seoTitle={seoTitle}
        description={ogDescription}
        ogDescription={ogDescription}
      />
      <MainBlackHeader title={downloadTitle}></MainBlackHeader>
      <div className={styles.outerContainer}>
        <div className={styles.innerContainer}>
          <div className={`${styles.selectOuterContainer}`}>
            <div className={`${styles.selectContainer} ${styles.large}`}>
              <h4>
                {hardwareVersion}
                <Tooltip content={TooltipHardwareHTML} />
              </h4>
              <Select
                defaultValue={mappedHardwareOptions[0]}
                onChange={handleHardwareChange}
                value={selectValueHardware}
                options={mappedHardwareOptions}
                isSearchable={false}
                styles={customStyles}
                autoFocus={false}
                components={{ DropdownIndicator }}
              />
            </div>

            <div className={`${styles.selectContainer} ${styles.large}`}>
              <h4>
                {firmwareVersion}
                <Tooltip content={TooltipFirmwareHTML} />
              </h4>
              <Select
                defaultValue={mappedFirmwareOptions[0]}
                onChange={handleFirmwareChange}
                options={mappedFirmwareOptions}
                value={selectValueFirmware}
                isSearchable={false}
                styles={customStyles}
                autoFocus={false}
                components={{ DropdownIndicator }}
                isOptionDisabled={isOptionDisabled}
              />
            </div>
          </div>

          {data[3]?.edges.map(
            ({ node }) =>
              node.downloadAlertText &&
              selectValueFirmware.value === node.value && (
                <div className={styles.downloadAlertContainer}>
                  <DownloadAlertBar
                    alert={node.downloadAlertText}
                    isHidden={false}
                  />
                </div>
              )
          )}

          <div className={`${styles.dataContainer}`}>
            <div className={`${styles.dataInnerContainer}`}>
              {Object.keys(filteredData).map((type) => (
                <div key={type}>
                  <DownloadList
                    title={type.toUpperCase()}
                    arr={
                      filteredData[type].labels.length > 1
                        ? filteredData[type].labels
                        : ['']
                    }
                    large
                  />
                  {filteredData[type].values.map((value, index) => (
                    <DownloadList
                      key={value.id}
                      title={value.title}
                      data={value.data}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.sensorContainer}>
            <SensorUpdateProcess
              title={sensorData.title}
              subtitle={sensorData.subtitle}
              components={sensorData.components}
              images={sensorData.images}
              container={true}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default DownloadCenter
